import { XMLBuilder, XMLParser } from 'fast-xml-parser';
import { MarkerType } from '@xyflow/react';
import type { AppEdge, AppNode, NodeKind } from '../types';
import { NODE_DEFAULTS, newId, sortParentsFirst } from '../store/flowStore';
import type { FlowDocument } from './customXml';

const builder = new XMLBuilder({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  format: true,
  indentBy: '  ',
  suppressEmptyNode: true,
});

function styleFor(kind: NodeKind, color: string): string {
  const base = `whiteSpace=wrap;html=1;fillColor=${color};strokeColor=#334155;fontColor=#ffffff;`;
  switch (kind) {
    case 'startEnd':
      return `ellipse;${base}`;
    case 'decision':
      return `rhombus;${base}`;
    case 'inputOutput':
      return `shape=parallelogram;perimeter=parallelogramPerimeter;fixedSize=1;${base}`;
    case 'annotation':
      return 'shape=note;whiteSpace=wrap;html=1;backgroundOutline=1;darkOpacity=0.05;fillColor=#FFF9B2;strokeColor=#d6b656;fontColor=#333333;';
    case 'group':
      return `rounded=1;whiteSpace=wrap;html=1;dashed=1;fillColor=none;strokeColor=${color};verticalAlign=top;fontColor=#666666;`;
    default:
      return `rounded=0;${base}`;
  }
}

const EDGE_STYLE =
  'edgeStyle=orthogonalEdgeStyle;rounded=1;orthogonalLoop=1;jettySize=auto;html=1;';

/**
 * Gera um XML no formato mxGraph (draw.io / diagrams.net).
 * O arquivo pode ser aberto diretamente em app.diagrams.net.
 */
export function exportToDrawioXml(
  nodes: AppNode[],
  edges: AppEdge[],
  name: string,
): string {
  const nodeCells = nodes.map((n) => ({
    '@_id': n.id,
    '@_value': n.data.label ?? '',
    '@_style': styleFor((n.type ?? 'process') as NodeKind, n.data.color ?? '#3b82f6'),
    '@_vertex': '1',
    '@_parent': n.parentId ?? '1',
    mxGeometry: {
      '@_x': String(n.position.x),
      '@_y': String(n.position.y),
      '@_width': String(n.measured?.width ?? n.width ?? 180),
      '@_height': String(n.measured?.height ?? n.height ?? 70),
      '@_as': 'geometry',
    },
  }));

  const edgeCells = edges.map((e) => ({
    '@_id': e.id,
    '@_value': e.data?.label ?? '',
    '@_style': EDGE_STYLE,
    '@_edge': '1',
    '@_parent': '1',
    '@_source': e.source,
    '@_target': e.target,
    mxGeometry: { '@_relative': '1', '@_as': 'geometry' },
  }));

  const xmlObject = {
    '?xml': { '@_version': '1.0', '@_encoding': 'UTF-8' },
    mxfile: {
      '@_host': 'app.diagrams.net',
      '@_type': 'device',
      diagram: {
        '@_id': 'flowchart-1',
        '@_name': name,
        mxGraphModel: {
          '@_dx': '800',
          '@_dy': '600',
          '@_grid': '1',
          '@_gridSize': '10',
          '@_guides': '1',
          '@_tooltips': '1',
          '@_connect': '1',
          '@_arrows': '1',
          '@_fold': '1',
          '@_page': '1',
          '@_pageScale': '1',
          '@_pageWidth': '1169',
          '@_pageHeight': '826',
          '@_math': '0',
          '@_shadow': '0',
          root: {
            mxCell: [
              { '@_id': '0' },
              { '@_id': '1', '@_parent': '0' },
              ...nodeCells,
              ...edgeCells,
            ],
          },
        },
      },
    },
  };

  return builder.build(xmlObject) as string;
}

/* ======================= Importação ======================= */

const drawioParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  isArray: (name) =>
    name === 'diagram' ||
    name === 'mxCell' ||
    name === 'object' ||
    name === 'UserObject',
  parseAttributeValue: false,
  parseTagValue: false,
  trimValues: false,
  allowBooleanAttributes: true,
});

type RawCell = Record<string, unknown>;

function cellAttr(cell: RawCell, name: string): string | undefined {
  const value = cell[`@_${name}`];
  return value === undefined ? undefined : String(value);
}

function cellNum(cell: RawCell | undefined, name: string): number | undefined {
  if (!cell) return undefined;
  const raw = cellAttr(cell, name);
  if (raw === undefined) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

/** Converte "chave=valor;token;..." do mxGraph em mapa (tokens soltos viram chave com valor vazio). */
function parseStyle(style: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const token of style.split(';')) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf('=');
    if (eq === -1) map.set(trimmed, '');
    else map.set(trimmed.slice(0, eq), trimmed.slice(eq + 1));
  }
  return map;
}

/** Mapeia o estilo mxGraph para os tipos do editor (espelho de styleFor). */
function kindFromStyle(style: Map<string, string>): NodeKind {
  if (style.has('ellipse')) return 'startEnd';
  if (style.has('rhombus')) return 'decision';
  if (style.get('shape') === 'parallelogram' || style.has('parallelogram')) {
    return 'inputOutput';
  }
  if (style.get('shape') === 'note') return 'annotation';
  if (style.has('group')) return 'group';
  if (style.get('fillColor') === 'none' && style.get('dashed') === '1') {
    return 'group';
  }
  return 'process';
}

/** Remove HTML dos labels do draw.io (html=1) preservando quebras de linha. */
function decodeLabel(raw: string): string {
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(div|p)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .trim();
}

/**
 * Faz o parse de um XML draw.io (mxGraph) não compactado.
 * Garante round-trip dos arquivos gerados por exportToDrawioXml e faz
 * best-effort para arquivos externos com formas comuns.
 * Lança Error com mensagem amigável.
 */
export function importFromDrawioXml(xml: string): FlowDocument {
  let parsed: Record<string, unknown>;
  try {
    parsed = drawioParser.parse(xml) as Record<string, unknown>;
  } catch {
    throw new Error('O arquivo não contém um XML válido.');
  }

  const mxfile = parsed.mxfile as Record<string, unknown> | undefined;
  const diagram = (mxfile?.diagram as RawCell[] | undefined)?.[0];

  let name = 'Fluxograma importado';
  let model: Record<string, unknown> | undefined;

  if (diagram) {
    name = cellAttr(diagram, 'name') ?? name;
    model = diagram.mxGraphModel as Record<string, unknown> | undefined;
    if (!model) {
      throw new Error(
        'Arquivo draw.io compactado não é suportado. No draw.io, desmarque "Arquivo compactado" ao salvar (XML não compactado).',
      );
    }
  } else {
    model = parsed.mxGraphModel as Record<string, unknown> | undefined;
  }

  if (!model) {
    throw new Error(
      'XML inválido: estrutura draw.io (mxGraphModel) não encontrada.',
    );
  }

  const root = model.root as Record<string, unknown> | undefined;

  const rawCells: RawCell[] = [];
  const collect = (list: unknown, wrapper = false) => {
    if (!Array.isArray(list)) return;
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const entry = item as RawCell;
      if (wrapper) {
        // <object label="..." id="..."><mxCell .../></object>
        const inner = (entry.mxCell as RawCell[] | undefined)?.[0];
        if (!inner) continue;
        rawCells.push({
          ...inner,
          '@_id': entry['@_id'] ?? inner['@_id'],
          '@_value': entry['@_label'] ?? inner['@_value'],
        });
      } else {
        rawCells.push(entry);
      }
    }
  };
  collect(root?.mxCell);
  collect(root?.object, true);
  collect(root?.UserObject, true);

  const vertexCells = rawCells.filter((c) => cellAttr(c, 'vertex') === '1');
  const edgeCells = rawCells.filter((c) => cellAttr(c, 'edge') === '1');

  const nodes: AppNode[] = [];
  const nodeIds = new Set<string>();
  const edgeLabelByParent = new Map<string, string>();

  for (const cell of vertexCells) {
    const style = parseStyle(cellAttr(cell, 'style') ?? '');

    // Labels de aresta viram rótulo da conexão pai, não nós
    if (style.has('edgeLabel')) {
      const parent = cellAttr(cell, 'parent');
      const label = decodeLabel(cellAttr(cell, 'value') ?? '');
      if (parent && label) edgeLabelByParent.set(parent, label);
      continue;
    }

    const id = cellAttr(cell, 'id') ?? newId();
    if (nodeIds.has(id)) continue;

    const kind = kindFromStyle(style);
    const defaults = NODE_DEFAULTS[kind];
    const geometry = cell.mxGeometry as RawCell | undefined;

    const fill = style.get('fillColor');
    const stroke = style.get('strokeColor');
    let color: string | undefined;
    if (kind === 'group') {
      color = stroke && stroke !== 'none' ? stroke : defaults.color;
    } else if (kind === 'annotation') {
      color = defaults.color;
    } else {
      color = fill && fill !== 'none' ? fill : defaults.color;
    }

    nodeIds.add(id);
    nodes.push({
      id,
      type: kind,
      position: {
        x: cellNum(geometry, 'x') ?? 0,
        y: cellNum(geometry, 'y') ?? 0,
      },
      width: cellNum(geometry, 'width') ?? defaults.width,
      height: cellNum(geometry, 'height') ?? defaults.height,
      parentId: cellAttr(cell, 'parent'),
      data: {
        label: decodeLabel(cellAttr(cell, 'value') ?? ''),
        color,
      },
    });
  }

  // parent só é válido quando referencia outro nó importado
  // (no draw.io, "0"/"1" são camadas internas)
  for (const node of nodes) {
    if (node.parentId && !nodeIds.has(node.parentId)) {
      node.parentId = undefined;
    }
  }

  if (nodes.length === 0) {
    throw new Error('Nenhum nó reconhecido no arquivo draw.io.');
  }

  const edges: AppEdge[] = [];
  const edgeIds = new Set<string>();

  for (const cell of edgeCells) {
    const id = cellAttr(cell, 'id') ?? newId();
    if (edgeIds.has(id)) continue;
    const source = cellAttr(cell, 'source');
    const target = cellAttr(cell, 'target');
    if (!source || !target || !nodeIds.has(source) || !nodeIds.has(target)) {
      continue;
    }
    edgeIds.add(id);
    const label =
      decodeLabel(cellAttr(cell, 'value') ?? '') ||
      edgeLabelByParent.get(id) ||
      '';
    edges.push({
      id,
      source,
      target,
      type: 'labeled',
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { label, pathType: 'smoothstep' },
    });
  }

  return { nodes: sortParentsFirst(nodes), edges, viewport: null, name };
}
